import type { DeepReadonly } from 'vue'

export interface OptionType {
  name: string
  value: string
}

export type InputType =
  | 'number'
  | 'select'
  | 'switch'

export interface NumberInputOptions {
  min?: number
  max?: number
  step?: number
}

export interface SwitchInputOptions {
  start?: OptionType
  end?: OptionType
}

export type ValueType<T extends InputType> = T extends 'number' ? number : T extends 'select' ? string : T extends 'switch' ? boolean : never

export type PredictionParameterDefinition<T extends InputType = InputType> = {
  name: string
  label: string
  required?: boolean
  info?: string
  defaultValue?: unknown
} & (T extends 'number' ? {
  type: 'number'
  options?: NumberInputOptions
  defaultValue?: number
} : T extends 'select' ? {
  type: 'select'
  options: OptionType[]
  defaultValue?: string
} : T extends 'switch' ? {
  type: 'switch'
  options?: SwitchInputOptions
  defaultValue?: boolean
} : never)

export type PredictionParameterDefinitions = DeepReadonly<PredictionParameterDefinition[]>

const currentDate = new Date()
const minEstimatedYearOfSettlement = currentDate.getFullYear()
const maxEstimatedYearOfSettlement = minEstimatedYearOfSettlement + 100

export default [
  {
    name: 'adjustedDiscount',
    label: 'Adjusted Discount (%)',
    type: 'number',
    options: {
      min: -2,
      max: 3,
      step: 0.25,
    },
    required: true,
  },
  {
    name: 'derivedInjury',
    label: 'Injury type',
    info: 'Pick from one of the drop-down options - in the event of multiple injuries, please pick the one which is the most disabling.',
    required: true,
    type: 'select',
    options: [
      {
        name: 'Amputation - Lower Limb Above Knee',
        value: 'amputation - lower limb above knee',
      },
      {
        name: 'Amputation - Lower Limb Below Knee',
        value: 'amputation - lower limb below knee',
      },
      {
        name: 'Amputation - Upper Limb',
        value: 'amputation - upper limb',
      },
      {
        name: 'Chronic Pain Syndrome',
        value: 'chronic pain syndrome',
      },
      {
        name: 'Orthopaedic - Ankle/Foot/Toe',
        value: 'orthopaedic - ankle/foot/toe',
      },
      {
        name: 'Orthopaedic - Back',
        value: 'orthopaedic - back',
      },
      {
        name: 'Orthopaedic - Leg',
        value: 'orthopaedic - leg',
      },
      {
        name: 'Orthopaedic - Pelvis and Hips',
        value: 'orthopaedic - pelvis and hips',
      },
      {
        name: 'Orthopaedic - Upper Limb',
        value: 'orthopaedic - upper limb',
      },
      {
        name: 'Paraplegic',
        value: 'paraplegic',
      },
      {
        name: 'Traumatic Brain Injury (TBI)',
        value: 'tbi',
      },
      {
        name: 'Tetraplegic',
        value: 'tetraplegic',
      },
      {
        name: 'Other',
        value: 'other',
      },
    ],
  },
  {
    name: 'ageAtAccident',
    label: 'Age at Accident',
    type: 'number',
    options: {
      min: 0,
      max: 200,
      step: 1,
    },
    defaultValue: 23,
  },
  {
    name: 'sex',
    label: 'Sex',
    type: 'switch',
    required: true,
    options: {
      start: {
        name: 'Male',
        value: 'male',
      },
      end: {
        name: 'Female',
        value: 'female',
      },
    },
  },
  {
    name: 'pegFeedingDays',
    label: 'PEG Feeding Days',
    type: 'switch',
    required: true,
    defaultValue: true,
    options: {
      start: {
        name: '0',
        value: '0',
      },
      end: {
        name: '> 0',
        value: '> 0',
      },
    },
  },
  {
    name: 'ventilationDays',
    label: 'Ventilation Days',
    type: 'switch',
    required: true,
    defaultValue: true,
    options: {
      start: {
        name: '0',
        value: '0',
      },
      end: {
        name: '> 0',
        value: '> 0',
      },
    },
  },
  {
    name: 'claimantFirm',
    label: 'Claimant Firm',
    type: 'select',
    defaultValue: 'unknown',
    options: [
      {
        name: 'Unknown',
        value: 'unknown',
      },
      {
        name: 'High Street',
        value: 'high street',
      },
      {
        name: 'Mid-Size',
        value: 'mid-size',
      },
      {
        name: 'National',
        value: 'national',
      },
    ],
  },
  {
    name: 'hospitalisationDays',
    label: 'Hospitalisation Days',
    info: 'The number of days the claimant was hospitalized following the accident.',
    type: 'number',
    defaultValue: 0,
    options: {
      min: 0,
      max: 73050,
    },
  },
  {
    name: 'levelOfSpinalCordInjury',
    label: 'Level of Spinal Cord Injury',
    type: 'select',
    defaultValue: '',
    options: [
      {
        name: 'Not applicable',
        value: 'not applicable',
      },
      {
        name: 'T',
        value: 't',
      },
      {
        name: 'L',
        value: 'l',
      },
      {
        name: 'C',
        value: 'c',
      },
    ],
  },
  {
    name: 'legalStatus',
    label: 'Legal Status',
    type: 'select',
    defaultValue: 'normal',
    options: [
      {
        name: 'Infant',
        value: 'infant',
      },
      {
        name: 'Normal',
        value: 'normal',
      },
      {
        name: 'Patient',
        value: 'patient',
      },
    ],
  },
  {
    name: 'reducedLifeExpectancy',
    label: 'Reduced Life Expectancy',
    type: 'switch',
    defaultValue: false,
    options: {
      start: {
        name: 'Yes',
        value: 'yes',
      },
      end: {
        name: 'No',
        value: 'no',
      },
    },
  },
  {
    name: 'glasgowComaScore',
    label: 'Glasgow Coma Score',
    type: 'select',
    defaultValue: '',
    options: [
      {
        name: 'Not applicable',
        value: 'not applicable',
      },
      {
        name: 'Minor [13...15]',
        value: 'minor [13...15]',
      },
      {
        name: 'Moderate [8...12]',
        value: 'moderate [8...12]',
      },
      {
        name: 'Severe [3...7]',
        value: 'severe [3...7]',
      },
    ],
  },
  {
    name: 'frontalLobeDamage',
    label: 'Frontal Lobe Damage',
    type: 'switch',
    defaultValue: false,
    options: {
      start: {
        name: 'Yes',
        value: 'yes',
      },
      end: {
        name: 'No',
        value: 'no',
      },
    },
  },
  {
    name: 'twentyFourHourCare',
    label: '24 Hour Care',
    type: 'switch',
    defaultValue: false,
    options: {
      start: {
        name: 'Yes',
        value: 'yes',
      },
      end: {
        name: 'No',
        value: 'no',
      },
    },
  },
  {
    name: 'proceedingsIssued',
    label: 'Proceedings Issued',
    type: 'switch',
    defaultValue: false,
    options: {
      start: {
        name: 'Yes',
        value: 'yes',
      },
      end: {
        name: 'No',
        value: 'no',
      },
    },
  },
  {
    name: 'claimsInflationRate',
    label: 'Claims Inflation Rate',
    type: 'number',
    required: true,
    options: {
      min: 0,
      max: 10,
      step: 1,
    },
  },
  {
    name: 'estimatedYearOfSettlement',
    label: 'EST. Year of Settlement',
    type: 'number',
    defaultValue: minEstimatedYearOfSettlement,
    options: {
      min: minEstimatedYearOfSettlement,
      max: maxEstimatedYearOfSettlement,
    },
  },
  {
    name: 'futureLossOfEarnings',
    label: 'Future Loss of Earnings',
    type: 'switch',
    options: {
      start: {
        name: 'Yes',
        value: 'yes',
      },
      end: {
        name: 'No',
        value: 'no',
      },
    },
  },
  {
    name: 'otherFutureLosses',
    label: 'Other Future Losses',
    type: 'switch',
    options: {
      start: {
        name: 'Yes',
        value: 'yes',
      },
      end: {
        name: 'No',
        value: 'no',
      },
    },
  },
] as const satisfies PredictionParameterDefinitions
